import { Card, Stack, Typography, Divider, Chip, Box } from "@mui/material";

// ----------------------------------------------------------------------

interface Props {
  stats: any;
}

// ----------------------------------------------------------------------

type StatusLevel = "Healthy" | "Warning" | "Critical";

function getChipColor(level: StatusLevel) {
  if (level === "Healthy") {
    return {
      bgcolor: "success.main",
      color: "common.white",
    };
  }

  if (level === "Warning") {
    return {
      bgcolor: "warning.main",
      color: "common.white",
    };
  }

  return {
    bgcolor: "error.main",
    color: "common.white",
  };
}

// ----------------------------------------------------------------------

export default function DashboardSummary({ stats }: Props) {
  const netProfit = stats?.netProfit || 0;
  const totalIncome = stats?.totalIncome || 0;
  const totalExpenditure = stats?.totalExpenditure || 0;
  const totalTransactions = stats?.totalTransactions || 0;

  // Use whichever field exists in your API
  const reconciledTransactions =
    stats?.reconciledTransactions ??
    stats?.reconciledCount ??
    0;

  const unclassifiedCount =
    stats?.unclassifiedCount ?? 0;

  const vatStatus =
    stats?.vatStatus ?? "Unknown";

  const reconciliationProgress =
    totalTransactions > 0
      ? (reconciledTransactions / totalTransactions) * 100
      : 0;

  const expenseRatio =
    totalIncome > 0
      ? (totalExpenditure / totalIncome) * 100
      : 0;

  // ----------------------------------------------------------------------
  // Overall Financial Status
  // ----------------------------------------------------------------------

  let overallStatus: StatusLevel = "Healthy";

  if (netProfit < 0) {
    overallStatus = "Critical";
  } else if (
    unclassifiedCount > 0 ||
    reconciliationProgress < 70 ||
    expenseRatio > 85
  ) {
    overallStatus = "Warning";
  }

  // ----------------------------------------------------------------------
  // Profitability
  // ----------------------------------------------------------------------

  const profitabilityLabel =
    netProfit > 0
      ? "Profitable"
      : netProfit === 0
      ? "Break-even"
      : "Loss-making";

  const profitabilityLevel: StatusLevel =
    netProfit > 0
      ? "Healthy"
      : netProfit === 0
      ? "Warning"
      : "Critical";

  // ----------------------------------------------------------------------
  // VAT Status
  // ----------------------------------------------------------------------

  const vatLevel: StatusLevel =
    vatStatus === "Filed" || vatStatus === "Up to date"
      ? "Healthy"
      : vatStatus === "Overdue"
      ? "Critical"
      : "Warning";

  // ----------------------------------------------------------------------
  // Reconciliation
  // ----------------------------------------------------------------------

  const reconciliationLevel: StatusLevel =
    reconciliationProgress >= 80
      ? "Healthy"
      : reconciliationProgress >= 50
      ? "Warning"
      : "Critical";

  // ----------------------------------------------------------------------
  // Unclassified Transactions
  // ----------------------------------------------------------------------

  const unclassifiedLevel: StatusLevel =
    unclassifiedCount === 0
      ? "Healthy"
      : "Warning";

  const unclassifiedLabel =
    unclassifiedCount === 0
      ? "All Classified"
      : `${unclassifiedCount} Pending`;

  // ----------------------------------------------------------------------
  // Recommendation
  // ----------------------------------------------------------------------
     const recommendations: string[] = [];

if (netProfit <= 0) {
  recommendations.push(
    "Business is currently operating at a loss. Review major expense categories."
  );
}

if (reconciliationProgress < 80) {
  recommendations.push(
    "Increase reconciliation to improve financial accuracy."
  );
}

if (unclassifiedCount > 20) {
  recommendations.push(
    `Categorise ${unclassifiedCount} outstanding transactions.`
  );
}

if (stats?.vatRecoverable > 0) {
  recommendations.push(
    `Recover ${stats.vatRecoverable.toFixed(2)} VAT before the reporting deadline.`
  );
}

if (recommendations.length === 0) {
  recommendations.push(
    "Financial performance is healthy. Continue monitoring trends."
  );
} 
      
      

  // ----------------------------------------------------------------------

  return (
    <Card sx={{ p: 3 }}>
      <Stack spacing={3}>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1}
        >
          <Typography variant="h6">
            Dashboard Summary
          </Typography>

          <Chip
            label={overallStatus}
            sx={{
              fontWeight: "bold",
              ...getChipColor(overallStatus),
            }}
          />
        </Stack>

        <Divider />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
            },
            gap: 2,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: "background.neutral",
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Profitability Status
            </Typography>

            <Chip
              size="small"
              label={profitabilityLabel}
              sx={{
                fontWeight: "bold",
                ...getChipColor(profitabilityLevel),
              }}
            />
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: "background.neutral",
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
            >
              VAT Status
            </Typography>

            <Chip
              size="small"
              label={vatStatus}
              sx={{
                fontWeight: "bold",
                ...getChipColor(vatLevel),
              }}
            />
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: "background.neutral",
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Reconciliation Progress
            </Typography>

            <Chip
              size="small"
              label={`${reconciliationProgress.toFixed(0)}%`}
              sx={{
                fontWeight: "bold",
                ...getChipColor(reconciliationLevel),
              }}
            />
          </Stack>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              p: 1.5,
              borderRadius: 1,
              bgcolor: "background.neutral",
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Unclassified Transactions
            </Typography>

            <Chip
              size="small"
              label={unclassifiedLabel}
              sx={{
                fontWeight: "bold",
                ...getChipColor(unclassifiedLevel),
              }}
            />
          </Stack>
        </Box>

        <Divider />
                <Box
          sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: "background.neutral",
          }}
        >
          <Stack spacing={1.5}>
            <Typography variant="subtitle2">
              Recommendations
            </Typography>

            {recommendations.map((item, index) => (
              <Typography
                key={index}
                variant="body2"
                color="text.secondary"
              >
                • {item}
              </Typography>
            ))}
          </Stack>
        </Box>
      </Stack>
    </Card>
  );
}